/**
 * THE FORK — master → project, on the one compose brain (Masters Ladder R5).
 *
 * `docs/MASTERS_LADDER_PLAN.md` §1.1: the project rung is THE FORK — a
 * composed variant copied whole into the project. Before R5 the copy was made by
 * a private flattener inside `AddSpecs.tsx` that knew only about legacy
 * variants. This module is the single replacement, and it is deliberately
 * thin: everything it does is `compose()` + `toExportBlocks()`, so the sheet
 * the Studio shows, the blocks a project receives and the blocks the engine
 * renders can never drift apart.
 *
 * Pure and dependency-free like the rest of `shared/` — the renderer imports
 * it directly, and tooling (the R5 golden harness) requires the compiled
 * `dist-main/shared/spec-fork.js`, so both prove the SAME function.
 *
 * Phase 2 (2026-08-25): the Selections axis is demolished. A fork is core
 * blocks (plus a client layer, when one is passed) with every dead ladder
 * stamp scrubbed off on the way out.
 */

import type { ClientLayer, ContentBlock, SpecMaster } from './spec';
import type { StandardConfig } from './types';
import { compose, stripMembership, toExportBlocks } from './spec-compose';
import { assembleSpec, type ModulePicks } from './spec-assemble';

/** The minimum a fork needs to know about a master. */
export type ForkableMaster = Pick<SpecMaster, 'id' | 'coreContent'>;

/** `keepModuleStamps`: Add to Project keeps module identity (Sync D1); every
 *  other dead stamp is still scrubbed. Exports leave it off (they rasterize). */
export interface ForkOptions {
  keepModuleStamps?: boolean;
}

/**
 * The blocks a project spec receives when this master is forked.
 *
 * @param master The office master.
 * @param layer  Optional client layer. Not passed by the Add Specs flow today
 *               (the project boundary is R7 scope); the parameter exists so
 *               the variant the Studio composes and the variant a project forks are
 *               the same call.
 */
export function forkMasterBlocks(
  master: ForkableMaster,
  layer?: ClientLayer,
  picks?: ModulePicks,
  numbering?: StandardConfig | null,
  opts?: ForkOptions
): ContentBlock[] {
  const composed = compose({ id: master.id, coreContent: master.coreContent }, layer);
  if (!picks) return toExportBlocks(composed, opts);
  // Module assembly runs BEFORE the strip so existing instances are still
  // stamped (excludable); the stamps are rasterized away at the end.
  return stripMembership(
    assembleSpec(visibleComposedBlocks(master, layer), picks, numbering),
    opts
  );
}

/** The master's visible composed blocks WITH module stamps (seed for the
 *  Modules dialog; also the input assembleSpec needs). */
export function visibleComposedBlocks(master: ForkableMaster, layer?: ClientLayer): ContentBlock[] {
  return compose({ id: master.id, coreContent: master.coreContent }, layer)
    .blocks.filter((c) => !c.hidden)
    .map((c) => c.block);
}
